import type {
  SnackBarConfig,
} from '@shared/components/Snackbar';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Chip, Tab, Tabs } from '@mui/material';
import { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ManageFollowedCompaniesButton } from '@shared/components/Buttons/FollowCompaniesButton';
import CompanyLogo from '@shared/components/CompanyLogo';
import CustomSnackbar from '@shared/components/Snackbar';
import StaticTextCard from '@shared/components/StaticTextCard';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import { UserType } from '@shared/domain/user';
import ChangePassword from './components/ChangePassword';
import OfferingsPage from './components/OfferingsPage';
import ProfilePage from './components/ProfilePage';

const SettingsPage = () => {
  const { user, userLoadState } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(searchParams.get('tab') ?? '0');
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  const navigate = useNavigate();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const handleSnackBarClose = () => {
    if (!snackbarConfig) return;
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  if (userLoadState === UserLoadState.None) {
    return <LoadingSpinner variant="circular" size="large" message="Loading account settings..." fullScreen />;
  }

  if (!user) {
    return (
      <StaticTextCard
        header="Not a Member!"
        text="It seems that you are not yet a member of Kandir, please head to our sign-up page."
        link={true}
      />
    );
  }

  const followingLabel = user.userType === UserType.COMPANY
    ? 'Users Following'
    : 'Followed Companies';

  return (
    <div className="account-settings-page">
      <header className="account-settings-page__header">
        <p className="account-settings-page__eyebrow">Account</p>
        <h1 className="account-settings-page__title">Account settings</h1>
        <p className="account-settings-page__subtitle">
          Manage your profile, offerings, followed companies, and password —
          all in one place.
        </p>
      </header>

      <div className="account-settings-page__card">
        <TabContext value={value}>
          <Box className="account-settings-page__tabs">
            <Tabs onChange={handleTabChange} value={value} variant="scrollable" scrollButtons="auto">
              <Tab label="Profile" value="0" data-testid="settings-tab-profile" />
              {user.userType !== UserType.COMPANY && user.planSlug !== 'contributor' && (
                <Tab label="Offerings" value="1" data-testid="settings-tab-offerings" />
              )}
              <Tab label={followingLabel} value="3" data-testid="settings-tab-following" />
              <Tab label="Security" value="4" data-testid="settings-tab-change-password" />
            </Tabs>
          </Box>

          <TabPanel value="0" className="account-settings-page__panel">
            <ProfilePage embedded={true} />
          </TabPanel>

          {user.userType !== UserType.COMPANY && user.planSlug !== 'contributor' && user.accountMemberships?.[0]?.accountId && (
            <TabPanel value="1" className="account-settings-page__panel">
              <OfferingsPage embedded={true} />
            </TabPanel>
          )}

          <TabPanel value="3" className="account-settings-page__panel">
            <h2 className="account-settings-page__section-title">{followingLabel}</h2>
            <p className="account-settings-page__section-subtitle">
              Follow companies you're selling to or interested in — Kandir
              surfaces relevant content and notifies you when something new
              lands.
            </p>

            {user.followedCompanies && user.followedCompanies.length === 0
              ? (
                  <div className="account-settings-page__followed-empty">
                    <h3 className="account-settings-page__followed-empty-title">
                      No companies followed yet
                    </h3>
                    <p className="account-settings-page__followed-empty-message">
                      Start following companies to see their profiles here and
                      get relevant content updates.
                    </p>
                  </div>
                )
              : (
                  <div className="account-settings-page__followed-chips">
                    {user.followedCompanies?.map(company => (
                      <Chip
                        key={company.id}
                        className="account-settings-page__followed-chip"
                        avatar={(
                          <CompanyLogo
                            companyId={company.id}
                            companyName={company?.companyName ?? ''}
                            companyUrl={company?.url ?? ''}
                            alt={company?.companyName}
                          />
                        )}
                        label={company.companyName}
                        variant="outlined"
                        onClick={() => {
                          navigate(`/company/${encodeURIComponent(company.companyName!)}`);
                        }}
                      />
                    ))}
                  </div>
                )}

            <div className="account-settings-page__followed-actions">
              <ManageFollowedCompaniesButton highlighted={!user.followedCompanies || user.followedCompanies.length === 0} />
            </div>
          </TabPanel>

          <TabPanel value="4" className="account-settings-page__panel">
            <h2 className="account-settings-page__section-title">Change password</h2>
            <p className="account-settings-page__section-subtitle">
              Pick a strong password — we recommend a passphrase plus a number
              and a symbol.
            </p>
            <ChangePassword />
          </TabPanel>
        </TabContext>
      </div>

      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </div>
  );
};

export default SettingsPage;
